/**
 * YouTube Music import — exact-ID resolution via ytmusicapi + yt-dlp.
 * Accepts only catalog audio-track entries (MUSIC_VIDEO_TYPE_ATV).
 * Never searches or substitutes another recording.
 */
import { z } from "zod";
const YTM_URL_RE = /^https?:\/\/(?:music\.youtube\.com\/(?:watch|playlist|browse)|(?:www\.)?youtube\.com\/watch|youtu\.be\/)/i;
export const YoutubeMusicImportBodySchema = z.object({
    url: z
        .string()
        .url()
        .refine((u) => YTM_URL_RE.test(u), {
        message: "Must be a YouTube Music playlist, album, or watch URL",
    }),
    owner: z.string().min(1),
});
export const YoutubeMusicVideoIdSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{11}$/, "Must be an 11-character YouTube video ID");
export const YoutubeMusicRejectReasonSchema = z.enum([
    "music_video",
    "unavailable",
    "missing_id",
    "download_failed",
    "unknown_type",
    "ambiguous_match",
    "unmatched",
]);
export const YoutubeMusicRejectedItemSchema = z.object({
    videoId: z.string().nullable(),
    title: z.string().nullable(),
    artists: z.string().nullable(),
    reason: YoutubeMusicRejectReasonSchema,
    videoType: z.string().nullable(),
});
export const YoutubeMusicPreflightStatusSchema = z.enum([
    "exact_existing",
    "legacy_matched",
    "ambiguous",
    "missing",
    "rejected",
]);
export const YoutubeMusicPreflightItemSchema = z.object({
    videoId: z.string().nullable(),
    title: z.string().nullable(),
    artists: z.string().nullable(),
    videoType: z.string().nullable(),
    status: YoutubeMusicPreflightStatusSchema,
    matchedTrackId: z.number().int().nullable(),
    rejectReason: YoutubeMusicRejectReasonSchema.nullable(),
});
export const YoutubeMusicJobStatusSchema = z.enum([
    "queued",
    "running",
    "completed",
    "failed",
    "cancelled",
]);
export const YoutubeMusicLinkBodySchema = z.object({
    trackId: z.number().int().nonnegative(),
    youtubeMusicVideoId: YoutubeMusicVideoIdSchema,
});
export const YoutubeMusicJobEventSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("log"), line: z.string() }),
    z.object({
        type: z.literal("progress"),
        completed: z.number(),
        total: z.number().nullable(),
    }),
    z.object({ type: z.literal("status"), status: YoutubeMusicJobStatusSchema }),
    z.object({ type: z.literal("track-added"), trackId: z.number() }),
    z.object({ type: z.literal("track-skipped"), trackId: z.number() }),
    z.object({ type: z.literal("track-updated"), trackId: z.number() }),
    z.object({ type: z.literal("track-removed"), trackId: z.number() }),
    z.object({
        type: z.literal("item-rejected"),
        item: YoutubeMusicRejectedItemSchema,
    }),
    z.object({
        type: z.literal("preflight"),
        items: z.array(YoutubeMusicPreflightItemSchema),
    }),
    z.object({
        type: z.literal("sync-summary"),
        added: z.number(),
        updated: z.number(),
        skipped: z.number(),
        removed: z.number(),
        rejected: z.number(),
        failed: z.number(),
        pruneBlocked: z.boolean(),
    }),
    z.object({ type: z.literal("error"), message: z.string() }),
    z.object({ type: z.literal("done") }),
]);
//# sourceMappingURL=youtubeMusic.js.map